import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useEffect, useState } from 'react';
import UIBox from 'UIComponents/layout/UIBox'; // @ts-expect-error dependency missing types

import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getUpgradeButtonText, getUpgradeButtonTextFromApiName } from 'ui-addon-upgrades/_core/common/adapters/getUpgradeButtonText';
import { USAGE_LIMIT_BANNER } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { ASSIGN_SEAT, BUY_TOUCHLESS, PRICING_PAGE_REDIRECT, REQUEST_SEAT } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import UIPricingPageRedirectButton from '../../button/UIPricingPageRedirectButton';
import { CRM_SUITE_STARTER_BUNDLE } from 'self-service-api/constants/ApiNames';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { CORE, SALES, SERVICE } from 'self-service-api/constants/UnitScopes';
import { SUITE_STARTER } from 'self-service-api/constants/UpgradeProducts';
import { getCorrectedUpgradeData } from '../../_core/utils/getCorrectedUpgradeData';
import displayAddOnLimitIncreaseCopy from '../displayAddOnLimitIncreaseCopy';
import getPrioritizedUpgradeProduct, { getPrioritizedUpgradeProductFromApiName } from '../getPrioritizedUpgradeProduct';
import { getApp, getBannerI18nSubTitleKey, getBannerI18nTitleKey, getHubAndTierFromApiName, getProductI18nNameFromApiName, getScreen, getSource, getSuiteStarterPurchaseMotions } from '../utils';
import DefaultBanner from './DefaultBanner';
export const buildBannerTitle = (featureTranslationKey, value, limit) => {
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: getBannerI18nTitleKey('upgrades.usageLimitBanner.header', featureTranslationKey),
    options: {
      value,
      limit
    }
  });
};

const buildSeatsCopy = (apiName, featureTranslationKey) => {
  const getSeatType = () => {
    const {
      hub,
      tier
    } = getHubAndTierFromApiName(apiName);

    if (tier === STARTER.toLocaleLowerCase()) {
      return `${CORE}_STARTER`;
    }

    if (hub === SALES.toLocaleLowerCase()) {
      return `${SALES}_${tier.toLocaleUpperCase()}`;
    } else if (hub === SERVICE.toLocaleLowerCase()) {
      return `${SERVICE}_${tier.toLocaleUpperCase()}`;
    }

    return `${CORE}_${tier.toLocaleUpperCase()}`;
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext.seats`, featureTranslationKey)
    }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
      message: `upgrades.seats.${getSeatType()}`
    }), "."]
  });
};

const buildAddOnCopy = (featureTranslationKey, translatedProductName) => {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
    }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
      message: `upgrades.usageLimitBanner.addOnLimitIncreaseOverride`,
      options: {
        productName: translatedProductName
      }
    })]
  });
};

export const buildBannerSubtext = (apiName, featureTranslationKey, canViewNewSeats, purchaseMotions) => {
  const translatedProductName = getProductI18nNameFromApiName('upgrades.products', apiName);
  const addOnCopyOverride = displayAddOnLimitIncreaseCopy(apiName);
  const seatsCopyOverride = purchaseMotions ? purchaseMotions.filter(pm => pm === ASSIGN_SEAT || pm === REQUEST_SEAT).length > 0 : false;

  const defaultContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
    }), "\xA0", translatedProductName && /*#__PURE__*/_jsx(FormattedMessage, {
      message: translatedProductName ? `upgrades.message.productName` : '',
      options: {
        productName: translatedProductName
      }
    })]
  });

  const subtextContent = () => {
    if (canViewNewSeats && seatsCopyOverride) {
      return buildSeatsCopy(apiName, featureTranslationKey);
    } else if (addOnCopyOverride) {
      return buildAddOnCopy(featureTranslationKey, translatedProductName || '');
    } else {
      return defaultContent;
    }
  };

  return /*#__PURE__*/_jsx(UIBox, {
    children: subtextContent()
  });
};

const UsageLimitBanner = ({
  upgradePoint,
  limit,
  value,
  displayThreshold,
  hasReachedLimit,
  className,
  style,
  'data-test-id': dataTestId = 'usage-limit-banner',
  purchaseMotionData,
  ownedProducts,
  canViewNewSeats = false,
  isFreePortal = false
}) => {
  const app = getApp();
  const screen = getScreen();
  const source = getSource(app, screen, upgradePoint.upgradeKey);
  const [purchaseMotions, setPurchaseMotions] = useState([]);
  const upgradeData = {
    app,
    screen,
    source,
    upgradeProduct: upgradePoint.upgradeProducts[0],
    upgradeProducts: upgradePoint.upgradeProducts,
    apiName: upgradePoint.apiName,
    apiNames: upgradePoint.apiNames,
    uniqueId: upgradePoint.upgradeKey,
    primaryColor: upgradePoint.primaryColor,
    team: upgradePoint.team,
    pointType: upgradePoint.pointType,
    status: upgradePoint.status
  };
  const purchaseMotionsFromData = purchaseMotionData && purchaseMotionData.locations && purchaseMotionData.locations.USAGE_LIMIT_BANNER && purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions ? purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions : null;

  const buildApiNamesToUse = () => {
    if (canViewNewSeats && upgradeData.apiNames) {
      return upgradeData.apiNames;
    } else if (upgradeData.upgradeProducts) {
      return upgradeData.upgradeProducts.map(product => UpgradeProductToApiNameMap[product]);
    }

    return [];
  };

  const apiNamesToUse = buildApiNamesToUse(); //if there is no purchase motion data fallback to default upgrade product

  upgradeData.upgradeProduct = purchaseMotionsFromData ? getPrioritizedUpgradeProduct(upgradePoint.upgradeProducts, purchaseMotionsFromData, ownedProducts, isFreePortal) : upgradePoint.upgradeProduct;
  const apiNameToUse = canViewNewSeats && upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct];

  if (canViewNewSeats) {
    upgradeData.apiName = purchaseMotionsFromData ? getPrioritizedUpgradeProductFromApiName(apiNamesToUse, purchaseMotionsFromData, ownedProducts, isFreePortal) : apiNameToUse;
  } else {
    upgradeData.apiName = apiNameToUse;
  }

  const correctedUpgradeData = Object.assign({
    feature: upgradePoint.feature,
    pointType: upgradePoint.pointType,
    upgradeKey: upgradePoint.upgradeKey
  }, getCorrectedUpgradeData(upgradeData));
  useEffect(() => {
    const fallbackPurchaseMotions = [PRICING_PAGE_REDIRECT];
    const apiName = canViewNewSeats && correctedUpgradeData.apiName ? correctedUpgradeData.apiName : UpgradeProductToApiNameMap[correctedUpgradeData.upgradeProduct];

    if (!apiName || !purchaseMotionData.locations || !purchaseMotionData.locations.USAGE_LIMIT_BANNER || !purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions) {
      setPurchaseMotions(fallbackPurchaseMotions);
    } else {
      // already have purchase motions from props so transform those instead of fetching
      const usageLimitBannerPMs = purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions;
      const rawPurchaseMotions = usageLimitBannerPMs[apiName];

      if (rawPurchaseMotions) {
        const transformedPurchaseMotions = Object.values(rawPurchaseMotions).flat().map(obj => obj ? obj.key : obj);
        setPurchaseMotions(transformedPurchaseMotions);
      } else {
        //apiName exists but there are no PMs defined for it
        if (apiName === CRM_SUITE_STARTER_BUNDLE) {
          setPurchaseMotions(getSuiteStarterPurchaseMotions(usageLimitBannerPMs, fallbackPurchaseMotions));
        } else {
          setPurchaseMotions(fallbackPurchaseMotions);
        }
      }
    }
  }, [correctedUpgradeData.upgradeProduct, purchaseMotionData, correctedUpgradeData.apiName, canViewNewSeats]);
  const usageLimitBannerData = {
    limit,
    value,
    displayThreshold,
    hasReachedLimit
  };

  const pricingPageRedirectButton = () => /*#__PURE__*/_jsx(UIPricingPageRedirectButton, {
    upgradeData: correctedUpgradeData,
    use: "tertiary-light",
    level: 1,
    isUpgradePoint: true,
    "data-test-id": "upgrade-button",
    usageLimitBannerData: usageLimitBannerData,
    goToSuiteStaterInterstitial: isFreePortal && (correctedUpgradeData.apiName === CRM_SUITE_STARTER_BUNDLE || correctedUpgradeData.upgradeProduct === SUITE_STARTER),
    children: canViewNewSeats && correctedUpgradeData.apiName ? getUpgradeButtonTextFromApiName('default', correctedUpgradeData.apiName) : getUpgradeButtonText('default', correctedUpgradeData.upgradeProduct)
  });

  const getUpgradeButton = () => {
    return /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
      upgradeData: correctedUpgradeData,
      usageLimitBannerData: usageLimitBannerData,
      location: USAGE_LIMIT_BANNER,
      level: 1,
      use: "tertiary-light",
      purchaseMotions: purchaseMotions,
      "data-test-id": "upgrade-button",
      overrides: {
        [BUY_TOUCHLESS]: pricingPageRedirectButton,
        //Adding this override because of the text changes in the CTA button for pricing page redirect
        [PRICING_PAGE_REDIRECT]: pricingPageRedirectButton
      }
    });
  };

  if (displayThreshold && value < displayThreshold) return null;
  return /*#__PURE__*/_jsx(DefaultBanner, {
    titleText: buildBannerTitle(upgradePoint.featureTranslationKey, value, limit),
    className: className,
    dataTestId: dataTestId,
    subtext: buildBannerSubtext(correctedUpgradeData.apiName || UpgradeProductToApiNameMap[correctedUpgradeData.upgradeProduct], upgradePoint.featureTranslationKey, canViewNewSeats, purchaseMotions),
    style: style,
    button: getUpgradeButton()
  });
};

export default UsageLimitBanner;